import { Box, Button, DialogActions, DialogContent, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { enqueueSnackbar } from "notistack";
import React, { useState } from "react";
import NumericTextfield from "src/components/shared/NumbericTextfield";
import useAppGlobal from "src/context/useAppGlobal";
import convertCurrency from "src/helper/convertCurrency";
import { formatCurrency } from "src/helper/convertNum";
import useDebtPayment from "src/hooks/debt/useDebtPayment";
import usePaymentAccountList from "src/hooks/debt/usePaymentAccountList";
import useInventoryNoteDetail from "src/hooks/inventory/useInventoryNoteDetail";

const PAYMENT_METHODS = [
  { value: "Transfer", label: "Chuyển khoản" },
  { value: "Cash", label: "Tiền mặt" },
];

export default function DebtSalesPayment({ item }) {
  const { closeDialog } = useAppGlobal();
  const { data: detail } = useInventoryNoteDetail(item?._id);
  const { data: bankInfoList } = usePaymentAccountList({ warehouse: item?.warehouse?._id });
  const { trigger: updateDebt } = useDebtPayment();

  const [innCode, setInnCode] = useState({
    paymentDate: dayjs(),
  });

  const handleChangeInnCode = (field, value) => {
    setInnCode((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const onPayment = async () => {
    const res = await updateDebt({
      body: {
        method: innCode?.method,
        totalReceived: innCode?.total,
        paymentDate: innCode?.paymentDate,
        note: innCode?.note,
        customer: detail?.data?.customer?._id,
        innCodes: [detail?.data?.code],
        bankInfo: innCode?.bankInfo,
        sales: detail?.data?.sales?._id,
      },
    });
    if (res?.success) {
      closeDialog();
      return enqueueSnackbar("Thàn công!", { variant: "success" });
    } else {
      return enqueueSnackbar(res?.error?.message, { variant: "error" });
    }
  };

  return (
    <>
      <DialogContent>
        <Stack gap={1.5}>
          <Box>
            <Typography
              variant="caption"
              color="grey"
            >
              Tên nhân viên kinh doanh
            </Typography>
            <Typography fontWeight="bold">{detail?.data?.sales?.fullName ?? "-"}</Typography>
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="grey"
            >
              Tên Khách hàng
            </Typography>
            <Typography fontWeight="bold">{detail?.data?.customer?.fullName ?? "-"}</Typography>
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="grey"
            >
              Tổng công nợ cần thanh toán
            </Typography>
            <Typography
              fontWeight="bold"
              color="error"
            >
              {formatCurrency(Math.abs((detail?.data?.total ?? 0) - (detail?.data?.customer?.balance ?? 0)))}
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontStyle: "italic" }}
            >
              ({convertCurrency(Math.abs((detail?.data?.total ?? 0) - (detail?.data?.customer?.balance ?? 0)))} đồng){" "}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="grey"
            >
              Số dư chưa gạt nợ
            </Typography>
            <Typography fontWeight="bold">{formatCurrency(detail?.data?.customer?.balance ?? 0)}</Typography>
            <Typography
              variant="caption"
              sx={{ fontStyle: "italic" }}
            >
              ({convertCurrency(detail?.data?.customer?.balance ?? 0)} đồng){" "}
            </Typography>
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            mt={1.5}
          >
            <DatePicker
              label="Ngày đi tiền trên nội dung chuyển khoản"
              format="DD/MM/YYYY"
              value={innCode.paymentDate}
              onChange={(newValue) => handleChangeInnCode("paymentDate", newValue ?? null)}
            />
            <NumericTextfield
              thousandSeparator
              label="Nhập số tiền thahnh toán"
              value={innCode.total}
              onChange={(values) => handleChangeInnCode("total", values?.floatValue ?? "")}
            />
            <FormControl fullWidth>
              <InputLabel id="payment-method-select-label">Hình thức thanh toán</InputLabel>
              <Select
                labelId="payment-method-select-label"
                id="payment-method-select"
                value={innCode.method}
                label="Hình thức thanh toán"
                onChange={(event) => handleChangeInnCode("method", event.target.value)}
              >
                {PAYMENT_METHODS.map((v) => {
                  return (
                    <MenuItem
                      key={v.value}
                      value={v.value}
                    >
                      {v.label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="payment-account-select-label">Số tài khoản nhận thanh toán</InputLabel>
              <Select
                labelId="payment-account-select-label"
                id="payment-account-select"
                value={innCode.bankInfo}
                label="Số tài khoản nhận thanh toán"
                onChange={(event) => handleChangeInnCode("bankInfo", event.target.value)}
              >
                {Array.isArray(bankInfoList?.data) ? (
                  bankInfoList?.data.map((v) => {
                    return (
                      <MenuItem
                        key={v._id}
                        value={v._id}
                      >
                        <Box sx={{ width: "45px", height: "45px", border: 1, borderRadius: 2, mr: 2, bgcolor: "white", display: "flex", alignItems: "center", p: 0.25 }}>
                          <img
                            style={{ width: "100%" }}
                            src={v?.bank?.logo}
                            alt=""
                          />
                        </Box>
                        {v.bank?.shortName} - {v?.ownerName} - {v?.accountNumber}
                      </MenuItem>
                    );
                  })
                ) : (
                  <MenuItem value="">Không tìm thấy dữ liệu</MenuItem>
                )}
              </Select>
            </FormControl>
            <TextField
              multiline
              rows={4}
              label="Ghi chú"
              value={innCode.note}
              onChange={(event) => handleChangeInnCode("note", event.target.value)}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: "0px 12px 24px" }}>
        <Button
          variant="contained"
          onClick={onPayment}
        >
          Thanh toán
        </Button>
      </DialogActions>
    </>
  );
}
